/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { Box, Chip, Stack, Typography } from "@mui/material";
import { dropzoneStyle } from "./dropzoneStyle";
import { CloudUpload } from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import { useEffect } from "react";
import { useSnackbar } from "notistack";

type DropzoneProps = {
  onChange: (files: File[]) => void;
  filesLimit?: number;
  dropzoneText?: string;
  maxFileSize?: number;
  fileObjects: File[];
  acceptedFiles?: string[];
  clearOnUnmount?: boolean;
  dropRejectMessage?: string;
  deletable?: boolean;
  error?: string | null;
};

export const Dropzone = ({
  dropzoneText,
  filesLimit = 1,
  acceptedFiles = [],
  fileObjects,
  onChange,
  maxFileSize,
  deletable = true,
  clearOnUnmount,
  dropRejectMessage,
  error,
}: DropzoneProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    maxFiles: filesLimit,
    accept: Object.fromEntries(acceptedFiles.map((fileType) => [fileType, []])),
    onDropAccepted: (newFiles) => {
      const combinedFiles = [...newFiles, ...fileObjects].slice(0, filesLimit);
      onChange(combinedFiles);
    },
    maxSize: maxFileSize,
    onDropRejected() {
      if (dropRejectMessage) {
        enqueueSnackbar(dropRejectMessage, {
          variant: "error",
        });
      }
    },
  });

  const handleDelete = (fileObject: File) => {
    onChange(fileObjects.filter((currentfileObject) => currentfileObject !== fileObject));
  };

  useEffect(() => {
    return () => {
      if (clearOnUnmount) {
        onChange([]);
      }
    };
  }, []);

  const hasError = !!error;

  return (
    <Stack sx={dropzoneStyle.container}>
      <Stack
        sx={[
          dropzoneStyle.dropzone,
          isDragAccept && dropzoneStyle.dropzoneDragAccept,
          isDragReject && dropzoneStyle.dropzoneDragReject,
          hasError && dropzoneStyle.dropzoneError,
        ]}
        {...getRootProps()}
      >
        <CloudUpload sx={dropzoneStyle.icon} />
        <Typography variant={"subtitle1"}>{dropzoneText}</Typography>
        <Box component={"input"} type={"file"} {...getInputProps()} />
      </Stack>
      <Stack direction={"row"} sx={dropzoneStyle.chips}>
        {fileObjects?.map((fileObject, idx) => {
          return (
            <Box key={idx}>
              <Chip
                label={fileObject.name}
                aria-label={fileObject.name + " löschen"}
                onDelete={deletable ? () => handleDelete(fileObject) : undefined}
              />
            </Box>
          );
        })}
      </Stack>
      {hasError && <Typography color="error">{error}</Typography>}
    </Stack>
  );
};
